import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import Loading from "./Loading";
import React, { Suspense, useEffect } from "react";
import UploadProgress from "./UploadProgress";
import RecordingUploadsPanel from "./RecordingUploadsPanel";
import { TourProvider } from "../onboarding/TutorialManager";
import Can from "./Can";
import SandboxBanner from "./SandboxBanner";
// import SandboxEmailGate from "./SandboxEmailGate";
import { isSandboxMode } from "../lib/sandbox";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { toggleDrawer } from "../store/slices/copilotSlice";
import { CopilotDrawer } from "./copilot/CopilotDrawer";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "./ui/resizable";
import AppSidebar from "./layout/AppSidebar";

// --- Lazy load components ---

const SubscriptionPlans = React.lazy(() => import("./SubscriptionPlans"));
const Dashboard = React.lazy(() => import("./debugger/Dashboard"));
const SubscriptionStatusCheck = React.lazy(
  () => import("./SubscriptionStatusCheck"),
);
const NotFound = React.lazy(() => import("./NotFound"));
const Home = React.lazy(() => import("./home/Home"));
const SettingsPage = React.lazy(() => import("./settings/SettingsPage"));
const ProfilePage = React.lazy(() => import("./settings/ProfilePage"));
const OrganizationPanel = React.lazy(
  () => import("./settings/OrganizationPanel"),
);
const UserManagementPanel = React.lazy(
  () => import("./settings/UserManagementPanel"),
);
const BillingSettings = React.lazy(() => import("./settings/BillingSettings"));
const SitesPage = React.lazy(() => import("./sites/SitesPage"));
const EnrollmentKeysPanel = React.lazy(
  () => import("./settings/EnrollmentKeysPanel"),
);
const NotificationChannelsPanel = React.lazy(
  () => import("./settings/NotificationChannelsPanel"),
);
const TemplateManager = React.lazy(() => import("./templates/TemplateManager"));
const ContactUsPage = React.lazy(() => import("./contact/ContactUsPage"));
const AlarmEditorPage = React.lazy(() => import("./alarms/AlarmEditorPage"));
const AlarmsDashboard = React.lazy(() => import("./alarms/AlarmsDashboard"));
const MonitoringDashboard = React.lazy(
  () => import("./monitoring-dashboard/MonitoringDashboard"),
);
const DeviceDetails = React.lazy(() => import("./device/DeviceDetails"));
const DeviceManagement = React.lazy(() => import("./device/DeviceManagement"));
const RecordingsPage = React.lazy(() => import("./recordings/RecordingsPage"));
const AddDevicePage = React.lazy(() => import("./device/add/AddDevicePage"));
const UploadedLogViewerPage = React.lazy(
  () => import("./recordings/UploadedLogViewerPage"),
);
const RecordingViewerPage = React.lazy(
  () => import("./device/details/recordings/RecordingViewerPage"),
);
const LearningCenterPage = React.lazy(
  () => import("./onboarding/LearningCenterPage"),
);

export default function AppContent() {
  // Expose react-router navigate to non-React modules (safe setter)
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isCopilotOpen = useAppSelector((s) => s.copilotReducer.isOpen);

  // Global keyboard shortcut: Ctrl+I (or Cmd+I on macOS) toggles the copilot drawer.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "i") {
        e.preventDefault();
        dispatch(toggleDrawer());
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [dispatch]);

  useEffect(() => {
    // lazy import to avoid cycles
    import("../lib/routerNavigator").then(({ setNavigate }) => {
      setNavigate(navigate as any);
      return () => {
        import("../lib/routerNavigator").then(({ clearNavigate }) =>
          clearNavigate(),
        );
      };
    });
  }, [navigate]);

  return (
    <>
      <TourProvider>
        {/* Rail + content sit side by side; the rail owns global navigation and
            account chrome, each page header owns its own title and actions. */}
        <div className="flex min-h-0 grow overflow-hidden">
          <AppSidebar />

          <div className="flex min-w-0 grow flex-col overflow-hidden">
            <SandboxBanner />
            {/* Email-gate overlay for sandbox visitors: shown after 3 min or on navigation away from /monitoring */}
            {/* {isSandboxMode() && <SandboxEmailGate />} */}
            <ResizablePanelGroup
              orientation="horizontal"
              className="grow overflow-hidden"
            >
              <ResizablePanel
                defaultSize={isCopilotOpen ? "70" : "100"}
                minSize={isCopilotOpen ? "40" : "0"}
              >
                {/* The app's scroll container paints its own background so the scrollable
                overflow area never falls through to the white page canvas. */}
                <div className="min-w-0 h-full overflow-auto bg-neutral-900">
                  <Suspense fallback={<Loading />}>
                    <SubscriptionStatusCheck>
                      <Routes>
                        <Route
                          path="/"
                          element={<Navigate to="/home" replace />}
                        />
                        <Route path="/home" element={<Home />} />
                        {/* Dashboard routes: include view in URL (/dashboard/topic or /dashboard/chat) */}
                        <Route
                          path="/dashboard"
                          element={<Navigate to="/dashboard/topic" replace />}
                        />
                        <Route
                          path="/dashboard/:view"
                          element={<Dashboard />}
                        />
                        <Route
                          path="/settings"
                          element={
                            isSandboxMode() ? (
                              <Navigate to="/home" replace />
                            ) : (
                              <SettingsPage />
                            )
                          }
                        >
                          <Route
                            index
                            element={<Navigate to="profile" replace />}
                          />
                          <Route path="profile" element={<ProfilePage />} />
                          <Route
                            path="organization"
                            element={<OrganizationPanel />}
                          />
                          <Route
                            path="users"
                            element={<UserManagementPanel />}
                          />
                          <Route
                            path="billing"
                            element={
                              <Can
                                perform="org:billing"
                                fallback={<Navigate to="/settings" replace />}
                              >
                                <BillingSettings />
                              </Can>
                            }
                          />
                          <Route
                            path="billing/change-plan"
                            element={
                              <Can
                                perform="org:billing"
                                fallback={<Navigate to="/settings" replace />}
                              >
                                <BillingSettings />
                              </Can>
                            }
                          />
                          <Route
                            path="enrollment-keys"
                            element={
                              <Can
                                perform="org:billing"
                                fallback={<Navigate to="/settings" replace />}
                              >
                                <EnrollmentKeysPanel />
                              </Can>
                            }
                          />
                          <Route
                            path="templates"
                            element={<TemplateManager />}
                          />
                          <Route
                            path="notifications"
                            element={
                              <Can
                                perform="dashboard:write"
                                fallback={<Navigate to="/settings" replace />}
                              >
                                <NotificationChannelsPanel />
                              </Can>
                            }
                          />
                          <Route
                            path="*"
                            element={<Navigate to="profile" replace />}
                          />
                        </Route>
                        <Route
                          path="/recordings/uploads/:sessionId/viewer"
                          element={<UploadedLogViewerPage />}
                        />
                        <Route
                          path="/devices/:deviceId/recordings/:recordingId/viewer"
                          element={<RecordingViewerPage />}
                        />
                        <Route path="/devices" element={<DeviceManagement />} />
                        <Route
                          path="/recordings"
                          element={<RecordingsPage />}
                        />
                        <Route
                          path="/devices/new"
                          element={
                            <Can
                              perform="device:write"
                              fallback={<Navigate to="/devices" replace />}
                            >
                              <AddDevicePage />
                            </Can>
                          }
                        />
                        <Route
                          path="/devices/:deviceId"
                          element={<DeviceDetails />}
                        />
                        <Route
                          path="/monitoring"
                          element={<MonitoringDashboard />}
                        />
                        <Route
                          path="/monitoring/:dashboardId"
                          element={<MonitoringDashboard />}
                        />
                        <Route
                          path="/monitoring/:dashboardId/panel/:panelId"
                          element={<MonitoringDashboard />}
                        />
                        {/* <Route path="/alarms" element={<AlarmConfiguration />} /> */}
                        <Route path="/alarms" element={<AlarmsDashboard />} />
                        <Route
                          path="/alarms/new"
                          element={<AlarmEditorPage />}
                        />
                        <Route
                          path="/alarms/:alarmId/edit"
                          element={<AlarmEditorPage />}
                        />
                        <Route path="/sites" element={<SitesPage />} />
                        <Route path="/contact-us" element={<ContactUsPage />} />
                        <Route
                          path="/learning-center"
                          element={<LearningCenterPage />}
                        />
                        <Route
                          path="/subscription-plans"
                          element={<SubscriptionPlans />}
                        />
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                      <UploadProgress />
                      <RecordingUploadsPanel />
                    </SubscriptionStatusCheck>
                  </Suspense>
                </div>
              </ResizablePanel>

              {/* Docked copilot panel, integrated into the shell layout. */}
              {isCopilotOpen && (
                <>
                  <ResizableHandle />
                  <ResizablePanel defaultSize="30" minSize="20">
                    <div className="h-full overflow-hidden border-l border-neutral-700/70 bg-neutral-950">
                      <CopilotDrawer />
                    </div>
                  </ResizablePanel>
                </>
              )}
            </ResizablePanelGroup>
          </div>
        </div>
      </TourProvider>
    </>
  );
}
